import { useSelector } from "react-redux"
import { useMemo } from "react"
import { getOpenerId } from "../utilities/session"
import {
	ATELIER_FILE_TYPE_APRES_REPARATION,
	ATELIER_FILE_TYPE_AUTRE,
	ATELIER_FILE_TYPE_AVANT_REPARATION,
	ATELIER_FILE_TYPE_BULLETIN_LIVRAISON,
	ATELIER_FILE_TYPE_CALCUL_REPARATION,
	ATELIER_FILE_TYPE_DEVIS,
	ATELIER_FILE_TYPE_DEVIS_SIGNE,
	ATELIER_FILE_TYPE_PHOTO_PIECE,
} from "../store/reducers/atelierReducer"
import {
	PAYE_PAR_ASSURANCE,
	PAYE_PAR_CLIENT,
} from "../components/atelier/AtelierLigneV2"
import { useGroups } from "./groups"
import { useRights } from "./rights"
import { detenteurFiche } from "./useFicheVerrou"
import { verrouFicheClose } from "../utilities/ficheClose"
import { ATELIER_ETAT_CLOS } from "../utilities/Addons"

export const useAtelierLignes = () => {
	const { atelierLignes } = useSelector((state) => state.atelierInfo)
	const { fournisseurs } = useSelector((state) => state.autreInfo)
	const getSousTraitants = useMemo(() => {
		let fournisseurs_ids = []
		atelierLignes
			.filter((al) => al.sousTraitant > 0)
			.forEach((al) => {
				if (!fournisseurs_ids.includes(al.sousTraitant)) {
					fournisseurs_ids.push(al.sousTraitant)
				}
			})
		let sousTraitants = []
		fournisseurs_ids.forEach((_id) => {
			sousTraitants.push(fournisseurs.find((f) => f.id === _id))
		})
		return sousTraitants
	}, [atelierLignes, fournisseurs])
	return {
		sousTraitants: getSousTraitants,
	}
}

export const useAtelierFiles = () => {
	const { files } = useSelector((state) => state.atelierInfo.atelier)
	const filesMap = useMemo(() => {
		let map = new Map()
		files.forEach((file) => {
			if (!map.has(file.type)) map.set(file.type, [])
			map.get(file.type).push(file)
		})
		return map
	}, [files])

	return {
		avantReparation: filesMap.get(ATELIER_FILE_TYPE_AVANT_REPARATION) || [],
		apresReparation: filesMap.get(ATELIER_FILE_TYPE_APRES_REPARATION) || [],
		calculReparation:
			filesMap.get(ATELIER_FILE_TYPE_CALCUL_REPARATION) || [],
		bulletinLivraison:
			filesMap.get(ATELIER_FILE_TYPE_BULLETIN_LIVRAISON) || [],
		devisSigne: filesMap.get(ATELIER_FILE_TYPE_DEVIS_SIGNE) || [],
		photoPiece: filesMap.get(ATELIER_FILE_TYPE_PHOTO_PIECE) || [],
		autre: filesMap.get(ATELIER_FILE_TYPE_AUTRE) || [],
		devis: filesMap.get(ATELIER_FILE_TYPE_DEVIS) || [],
		filesMap,
	}
}

// Référence stable pour éviter les re-renders quand aucune transaction n'est chargée.
const EMPTY_TRANSACTIONS = []

// Présence d'un document sur une transaction. Deux formes selon la source :
// V1 (ateTransactions) → { url, saved }, V2 (transactionsv2) → { url, uploaded }
// (null si absent). On accepte les deux pour ne pas désactiver à tort le choix.
const hasDoc = (doc) => !!doc && (doc.saved || doc.uploaded)

export const useAtelierTransactionFiles = () => {
	// Hook partagé par EmailModal (V1, ateTransactions) et EmailModalV2
	// (transactionsv2). On prend la liste V2 si elle est peuplée, sinon la V1.
	const transactions = useSelector((state) => {
		const a = state.atelierInfo.atelier
		if (a.transactionsv2 && a.transactionsv2.length) return a.transactionsv2
		return a.ateTransactions || EMPTY_TRANSACTIONS
	})
	const hasClientFactures = useMemo(
		() =>
			transactions.some(
				(t) => t.payeParSelect === PAYE_PAR_CLIENT && hasDoc(t.facture),
			),
		[transactions],
	)
	const hasClientQuittances = useMemo(
		() =>
			transactions.some(
				(t) =>
					t.payeParSelect === PAYE_PAR_CLIENT && hasDoc(t.quittance),
			),
		[transactions],
	)
	const hasAssuranceFactures = useMemo(
		() =>
			transactions.some(
				(t) =>
					t.payeParSelect === PAYE_PAR_ASSURANCE && hasDoc(t.facture),
			),
		[transactions],
	)
	const hasAssuranceQuittances = useMemo(
		() =>
			transactions.some(
				(t) =>
					t.payeParSelect === PAYE_PAR_ASSURANCE &&
					hasDoc(t.quittance),
			),
		[transactions],
	)
	return {
		hasClientFactures,
		hasClientQuittances,
		hasAssuranceFactures,
		hasAssuranceQuittances,
	}
}

export const useAtelierPermissions = () => {
	const {
		isBoss,
		isAdmin,
		isResponsableAtelier,
		isResponsableDepannage,
		isAdminAtelier,
		isAdminDepannage,
	} = useGroups()
	const { hasRight } = useRights()
	const { atelier } = useSelector((state) => state.atelierInfo)
	const { fichesOuvertes } = useSelector((state) => state.fichesOuvertesInfo)
	const groupes = useSelector((state) => state.navInfo.login.groups)

	const canSeeAdmin = isAdmin || isBoss
	const canSeeChefAtelier = isBoss || isAdmin || isResponsableAtelier
	// Visibilité de la carte « Heures de travail » : responsable atelier +
	// admin_atelier.
	const canSeeCarteHeures = canSeeChefAtelier || isAdminAtelier
	// Clôture (matrice serveur, qui refuse aussi le save — svp/access.py) :
	// fiche normale = dernière étape du flux facturation (boss + admin +
	// assistant_admin) ; fiche d'entretien (svp) = boss + responsable_atelier
	// + admin_atelier, exclusivement.
	const canClore = hasRight(
		atelier.svp ? "atelier_cloture_entretien" : "atelier_cloture",
	)
	// Jordan 30.10 : « Facturer » (boss, admin, assistant_admin) séparé de la
	// clôture — sans `canClore`, la facturation ne clôt jamais.
	const canFacturer = hasRight("facturer_fiche")
	// Affichage du prix d'achat et des marges dans les fiches (ligne « Atelier :
	// affichage marge/prix achat » de la matrice Excel) : tous les rôles SAUF
	// assistant_admin et employe. ⚠ Avant le 30.07.2026, les employés le
	// voyaient (`admin || personnel_atelier`) — c'est un retrait volontaire.
	const canSeePrixAchat =
		isBoss ||
		isAdmin ||
		isResponsableAtelier ||
		isResponsableDepannage ||
		isAdminAtelier ||
		isAdminDepannage

	// Verrou par SESSION (et non par utilisateur) : la fiche est en lecture seule
	// si elle est détenue par une autre session/onglet — y compris du même user.
	// La règle vient de `detenteurFiche` (useFicheVerrou) pour qu'il n'y en ait
	// qu'une ; on garde la forme « liste » attendue par les appelants.
	const openerId = getOpenerId()
	const isFicheOuverte = useMemo(() => {
		const detenteur = detenteurFiche(
			fichesOuvertes,
			"ate",
			atelier.id,
			openerId,
		)
		return detenteur ? [detenteur] : []
	}, [atelier.id, fichesOuvertes, openerId])

	// Édition des cartes/champs :
	// - détenue par une AUTRE session → lecture seule absolue (verrou concurrent) ;
	// - fiche CLOSE (Jordan 30.10, utilities/ficheClose) → lecture seule sauf
	//   pour le boss ; admin / admin_atelier / responsable_atelier gardent
	//   Commentaires, Sous-traitance et E-mails (`canEditBlocs`) ;
	// - sinon → tout éditable (les champs dispo restent gérés par état/groupe).
	const sessionLocked = isFicheOuverte.length > 0
	// GE-67, Q6 provisoire (Jordan 10.10) : fiche issue d'un dépannage dont
	// la mission n'est pas clôturée → lecture seule (le serveur refuse aussi,
	// 409) ; seule la bascule de client reste possible.
	// GE-69 : une fiche de RÉPARATION n'est jamais verrouillée.
	const fournituresEnCours = !!(
		atelier.issue_depannage &&
		atelier.issue_depannage.type !== "reparation" &&
		!atelier.issue_depannage.mission_cloturee
	)
	const ouverte = !sessionLocked && !fournituresEnCours
	const verrouClose = verrouFicheClose({
		close: atelier.etat === ATELIER_ETAT_CLOS,
		groupes,
		module: "atelier",
	})
	const canEdit = ouverte && !verrouClose.fiche
	// Blocs ouverts d'une fiche close (rôles du tableau) : Commentaires,
	// Sous-traitance, E-mails — et Sauvegarder pour les enregistrer.
	const canEditBlocs = ouverte && !verrouClose.blocs
	// Blocs Travaux / Heures / Transactions : verrouillés en Clos sauf boss.
	const canEditFinancier = canEdit
	// Sélecteur d'état en Clos (réouverture) : boss seul (Jordan 30.10).
	const canEditEtat = canEdit
	const canSave = canEditBlocs

	return {
		isBoss,
		canSeeAdmin,
		canSeeChefAtelier,
		canSeeCarteHeures,
		canSeePrixAchat,
		canEdit,
		canEditBlocs,
		ficheCloseVerrouillee: verrouClose.fiche,
		canEditFinancier,
		canEditEtat,
		canSave,
		fournituresEnCours,
		isFicheOuverte,
		canClore,
		canFacturer,
	}
}
